import React, { useState } from "react";

import { ORDER_MODIFICATION_SUMMARY_COLUMNS } from "../../constants/config";
import Table from "../../widgets/Table";
import PrimaryButton from "widgets/Buttons/PrimaryButton";
import SecondaryButton from "widgets/Buttons/SecondaryButton";
import ConfirmOrderUpdateDialog from "../ConfirmOrderUpdateDialog/ConfirmOrderUpdateDialog";
import DiscardChangesDialog from "../DiscardChangesDialog/DiscardChangesDialog";
import { useOrderModificationDecision } from "hooks/apiHooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import routes from "../../constants/routes";
import { formatAddress } from "utils/formatAddress";
import { getVerticalIdSearchFromParams } from "utils/commonUtils";
import AlreadyModifiedOrderDialog from "components/AlreadyModifiedOrderDialog/AlreadyModifiedOrderDialog";
import { toast } from "react-toastify";
import { ALREADY_MODIFIED_ERROR_CODE } from "constants/config";

import styles from "./OrderModificationSummary.module.scss";

const formatCurrency = (value) =>
  typeof value === "number" ? `₹ ${value.toLocaleString()}` : "N/A";

const getOrderModificationSummaryRows = (orderModificationSummary) => {
  if (!orderModificationSummary) {
    return [];
  }
  const current = orderModificationSummary.current_order || {};
  const proposal = orderModificationSummary.proposal_order || {};

  const formatPackages = (packages) =>
    packages?.map((p) => p.name).join("\n") || "N/A";

  return [
    {
      description: "Course(s)",
      currentOrder: formatPackages(current.packages),
      proposedOrder: formatPackages(proposal.packages),
    },
    {
      description: "Order Amount",
      currentOrder: formatCurrency(current.amount),
      proposedOrder: formatCurrency(proposal.amount),
    },
    {
      description: "Billing Name",
      currentOrder: current.billing_address?.recipient_name || "N/A",
      proposedOrder: proposal.billing_address?.recipient_name || "N/A",
    },
    {
      description: "Billing Address",
      currentOrder: formatAddress(current.billing_address),
      proposedOrder: formatAddress(proposal.billing_address),
    },
    {
      description: "Shipping Name",
      currentOrder: current.shipping_address?.recipient_name || "N/A",
      proposedOrder: proposal.shipping_address?.recipient_name || "N/A",
    },
    {
      description: "Shipping Address",
      currentOrder: formatAddress(current.shipping_address),
      proposedOrder: formatAddress(proposal.shipping_address),
    },
  ];
};

const getMutationErrorMessage = (error) =>
  error?.response?.data?.message ?? error?.message ?? "Something went wrong";

const handleConfirmOrderModification = (
  orderModificationDecision,
  navigate,
  verticalIdSearch,
  orderId,
  proposalId,
  orderModificationSummary,
  setAlreadyModifiedError,
  setIsConfirmOpen,
  setIsDiscardOpen,
) => {
  const payload = {
    order_id: orderId,
    proposal_id: proposalId,
    operation: "ACCEPT",
  };
  orderModificationDecision(payload, {
    onSuccess: () => {
      navigate({
        pathname: routes.modifiedOrderSummary,
        search: verticalIdSearch,
        state: {
          proposedOrder: orderModificationSummary?.proposal_order,
        },
      });
      toast.success("Order modification request accepted successfully");
    },
    onError: (error) => {
      const errorCode = error.errorCode;
      if (
        errorCode === ALREADY_MODIFIED_ERROR_CODE.PROPOSAL_ALREADY_ACCEPTED ||
        errorCode === ALREADY_MODIFIED_ERROR_CODE.PROPOSAL_ALREADY_REJECTED
      ) {
        setAlreadyModifiedError(errorCode);
        setIsConfirmOpen(false);
        setIsDiscardOpen(false);
        return;
      }
      toast.error(getMutationErrorMessage(error));
    },
  });
};

const handleRejectOrderModification = (
  orderModificationDecision,
  navigate,
  verticalIdSearch,
  orderId,
  proposalId,
  setAlreadyModifiedError,
  setIsConfirmOpen,
  setIsDiscardOpen,
) => {
  const payload = {
    order_id: orderId,
    proposal_id: proposalId,
    operation: "REJECT_BY_CUSTOMER",
  };
  orderModificationDecision(payload, {
    onSuccess: (response) => {
      navigate({
        pathname: routes.rejectedOrderModification,
        search: verticalIdSearch,
      });
      toast.success("Order modification request rejected successfully");
    },
    onError: (error) => {
      const errorCode = error.errorCode;
      if (
        errorCode === ALREADY_MODIFIED_ERROR_CODE.PROPOSAL_ALREADY_ACCEPTED ||
        errorCode === ALREADY_MODIFIED_ERROR_CODE.PROPOSAL_ALREADY_REJECTED
      ) {
        setAlreadyModifiedError(errorCode);
        setIsConfirmOpen(false);
        setIsDiscardOpen(false);
        return;
      }
      toast.error(getMutationErrorMessage(error));
    },
  });
};
const handleOpenConfirm = (setIsConfirmOpen) => {
  setIsConfirmOpen(true);
};
const handleCloseConfirm = (setIsConfirmOpen) => {
  setIsConfirmOpen(false);
};
const handleOpenDiscard = (setIsDiscardOpen) => {
  setIsDiscardOpen(true);
};
const handleCloseDiscard = (setIsDiscardOpen) => {
  setIsDiscardOpen(false);
};

const OrderModificationSummary = (props) => {
  const { orderModificationSummary } = props;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verticalIdSearch = getVerticalIdSearchFromParams(searchParams);
  const rows = getOrderModificationSummaryRows(orderModificationSummary);
  const paymentSummary = orderModificationSummary?.payment_summary || {};
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [alreadyModifiedError, setAlreadyModifiedError] = useState(false);

  const {
    mutate: orderModificationDecision,
    isPending: isOrderModificationDecisionLoading,
  } = useOrderModificationDecision();

  const orderId = orderModificationSummary?.current_order?.id;
  const proposalId = orderModificationSummary?.proposal_order?.id;

  return (
    <div className={styles.orderModificationSummaryMainContainer}>
      <div className={styles.headerText}>
        <span className={styles.registeredNameHeader}>
          Hi {orderModificationSummary?.registered_name},
        </span>
        <span>
          Please review the proposed updates below and provide your confirmation
          to apply these changes to your order.
        </span>
      </div>
      <Table columns={ORDER_MODIFICATION_SUMMARY_COLUMNS} rows={rows} />
      <div className={styles.paymentSummary}>
        <div className={styles.paymentRow}>
          <span className={styles.paymentLabel}>Adjusted Payment</span>
          <span className={styles.paymentColon}>:</span>
          <span className={styles.paymentValue}>
            {formatCurrency(paymentSummary.payment_to_be_adjusted)}
          </span>
        </div>
        <div className={styles.paymentRow}>
          <span className={styles.paymentLabel}>
            Remaining payment to collect
          </span>
          <span className={styles.paymentColon}>:</span>
          <span className={styles.paymentValue}>
            {formatCurrency(paymentSummary.remaining_payment_to_collect)}
          </span>
        </div>
      </div>
      <div className={styles.disclaimerText}>
        Any excess amount resulting from payment adjustments or payment updates
        will be refunded shortly. You may reach out to our customer support team
        at <span className={styles.supportNumber}>1800-570-6262</span> for
        further assistance.
      </div>
      <div className={styles.buttonContainer}>
        <SecondaryButton onClick={() => handleOpenDiscard(setIsDiscardOpen)}>
          Do not update my order
        </SecondaryButton>
        <PrimaryButton onClick={() => handleOpenConfirm(setIsConfirmOpen)}>
          Confirm & update my order
        </PrimaryButton>
      </div>
      <ConfirmOrderUpdateDialog
        open={isConfirmOpen}
        onClose={() => handleCloseConfirm(setIsConfirmOpen)}
        isLoading={isOrderModificationDecisionLoading}
        onConfirm={() =>
          handleConfirmOrderModification(
            orderModificationDecision,
            navigate,
            verticalIdSearch,
            orderId,
            proposalId,
            orderModificationSummary,
            setAlreadyModifiedError,
            setIsConfirmOpen,
            setIsDiscardOpen,
          )
        }
      />
      <DiscardChangesDialog
        open={isDiscardOpen}
        onClose={() => handleCloseDiscard(setIsDiscardOpen)}
        isLoading={isOrderModificationDecisionLoading}
        onConfirm={() =>
          handleRejectOrderModification(
            orderModificationDecision,
            navigate,
            verticalIdSearch,
            orderId,
            proposalId,
            setAlreadyModifiedError,
            setIsConfirmOpen,
            setIsDiscardOpen,
          )
        }
      />
      <AlreadyModifiedOrderDialog
        open={!!alreadyModifiedError}
        errorCode={alreadyModifiedError}
        onClose={() => setAlreadyModifiedError(null)}
        onOkay={() => {
          if (alreadyModifiedError === ALREADY_MODIFIED_ERROR_CODE.PROPOSAL_ALREADY_ACCEPTED) {
            navigate({
              pathname: routes.modifiedOrderSummary,
              search: verticalIdSearch,
              state: {
                proposedOrder: orderModificationSummary?.proposal_order,
              },
            });
          } else if (alreadyModifiedError === ALREADY_MODIFIED_ERROR_CODE.PROPOSAL_ALREADY_REJECTED) {
            navigate({
              pathname: routes.rejectedOrderModification,
              search: verticalIdSearch,
            });
          }

          setAlreadyModifiedError(null);
        }}
      />
    </div>
  );
};

export default OrderModificationSummary;
